import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useImages } from '../../hooks/useImages'
import { publicApiClient } from '../../api/client'
import type { Image } from '../../types/image'
import { GalleryItem } from './GalleryItem'
import { DateHeader } from './DateHeader'
import { groupImagesByDate, getDateImageGroups } from '../../lib/dateGroups'
import type { GroupedItem } from '../../lib/dateGroups'
import { Lightbox } from './Lightbox'
import { ShimmerSkeleton } from '../common/ShimmerSkeleton'
import { Loading } from '../common/Loading'
import { LayoutToggle } from './LayoutToggle'
import { MasonryGroup } from './MasonryGroup'
import { BulkDownloadBar } from './BulkDownloadBar'
import { CheckSquare, Square } from 'lucide-react'

const LAYOUT_STORAGE_KEY = 'gallery-layout-mode'

function getSavedLayout(): 'grid' | 'masonry' {
  try {
    const saved = localStorage.getItem(LAYOUT_STORAGE_KEY)
    if (saved === 'masonry') return 'masonry'
    return 'grid'
  } catch {
    return 'grid'
  }
}

interface GalleryGridProps {
  albumId?: string
  sortOrder?: 'asc' | 'desc'
  groupByDate?: boolean
  shareToken?: string
}

export const GalleryGrid: React.FC<GalleryGridProps> = ({ albumId, sortOrder, groupByDate, shareToken }) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useImages(publicApiClient, {
    albumId,
    sortOrder,
    limit: 50,
    shareToken,
  })

  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isIntersecting, setIsIntersecting] = useState(false)
  const [layoutMode, setLayoutMode] = useState<'grid' | 'masonry'>(getSavedLayout)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const observerRef = useRef<IntersectionObserver | null>(null)

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    if (!node) return

    observerRef.current = new IntersectionObserver(
      ([entry]) => setIsIntersecting(entry.isIntersecting),
      { rootMargin: '200px' }
    )

    observerRef.current.observe(node)
  }, [])

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage()
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage])

  const handleLayoutChange = (mode: 'grid' | 'masonry') => {
    setLayoutMode(mode)
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, mode)
    } catch {
      // Ignore if localStorage is unavailable
    }
  }

  const images = useMemo(() => {
    const seen = new Set<string>()
    return data?.pages.flatMap((page) => page.items).filter((image) => {
      if (seen.has(image.imageId)) {
        return false
      }
      seen.add(image.imageId)
      return true
    }) || []
  }, [data?.pages])

  // Prune stale selected IDs when images change (e.g., after pagination)
  useEffect(() => {
    const validIds = new Set(images.map((img) => img.imageId))
    setSelectedIds((prev) => {
      const next = new Set(prev)
      let changed = false
      for (const id of prev) {
        if (!validIds.has(id)) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [images])

  // Build a map of selected images for the bulk download bar
  const selectedImagesMap = useMemo(() => {
    const map = new Map<string, Image>()
    const validIds = new Set(images.map((img) => img.imageId))
    for (const id of selectedIds) {
      if (validIds.has(id)) {
        const image = images.find((img) => img.imageId === id)
        if (image) map.set(id, image)
      }
    }
    return map
  }, [selectedIds, images])

  const imageIndexMap = useMemo(() => {
    const map = new Map<string, number>()
    images.forEach((img, idx) => map.set(img.imageId, idx))
    return map
  }, [images])

  const groupedItems: GroupedItem[] = useMemo(() => {
    if (!groupByDate) return []
    return groupImagesByDate(images)
  }, [images, groupByDate])

  const dateGroups = useMemo(() => {
    if (!groupByDate) return []
    return getDateImageGroups(images)
  }, [images, groupByDate])

  // Determine if we're in "selection mode" (user has selected at least one photo)
  const hasSelection = selectedIds.size > 0

  const handleToggleSelect = useCallback((imageId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(imageId)) {
        next.delete(imageId)
      } else {
        next.add(imageId)
      }
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      // If all currently-loaded images are already selected, deselect all
      const allLoadedIds = new Set(images.map((img) => img.imageId))
      const allSelected = images.every((img) => prev.has(img.imageId))
      if (allSelected && prev.size === allLoadedIds.size) {
        return new Set()
      }
      // Otherwise, select all currently loaded images
      return new Set([...prev, ...allLoadedIds])
    })
  }, [images])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const handleImageClick = useCallback(
    (index: number) => {
      setCurrentImageIndex(index)
      setLightboxOpen(true)
    },
    [],
  )

  const handleLightboxNavigate = (index: number) => {
    setCurrentImageIndex(index)
  }

  const handleLightboxClose = () => {
    setLightboxOpen(false)
  }

  const renderToolbar = () => (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">
          {images.length} photo{images.length !== 1 ? 's' : ''}
        </span>
        {hasSelection && (
          <span className="text-sm font-medium text-blue-600">
            {selectedIds.size} selected
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* Select / Deselect all button */}
        {images.length > 0 && (
          <button
            onClick={handleSelectAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label={
              hasSelection && selectedIds.size === images.length
                ? 'Deselect all'
                : 'Select all loaded photos'
            }
          >
            {hasSelection && selectedIds.size === images.length ? (
              <>
                <CheckSquare className="w-4 h-4" />
                Deselect all
              </>
            ) : (
              <>
                <Square className="w-4 h-4" />
                Select all
              </>
            )}
          </button>
        )}
        <LayoutToggle value={layoutMode} onChange={handleLayoutChange} />
      </div>
    </div>
  )

  const renderGrid = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3">
      {groupByDate
        ? groupedItems.map((item) =>
            item.type === 'header' ? (
              <DateHeader key={`header-${item.date}`} date={item.date} />
            ) : (
              <GalleryItem
                key={item.image.imageId}
                image={item.image}
                onClick={() => handleImageClick(item.index)}
                isSelected={selectedIds.has(item.image.imageId)}
                onToggleSelect={() => handleToggleSelect(item.image.imageId)}
                selectionMode={hasSelection}
              />
            ),
          )
        : images.map((image, index) => (
            <GalleryItem
              key={image.imageId}
              image={image}
              onClick={() => handleImageClick(index)}
              isSelected={selectedIds.has(image.imageId)}
              onToggleSelect={() => handleToggleSelect(image.imageId)}
              selectionMode={hasSelection}
            />
          ))}
    </div>
  )

  const renderMasonry = () => {
    if (groupByDate) {
      return (
        <div className="space-y-8">
          {dateGroups.map((group) => (
            <div key={group.date}>
              <DateHeader date={group.date} />
              <MasonryGroup
                images={group.images}
                onImageClick={handleImageClick}
                getIndex={(img) => imageIndexMap.get(img.imageId) ?? 0}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                selectionMode={hasSelection}
              />
            </div>
          ))}
        </div>
      )
    }

    return (
      <MasonryGroup
        images={images}
        onImageClick={handleImageClick}
        getIndex={(img) => imageIndexMap.get(img.imageId) ?? 0}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        selectionMode={hasSelection}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3">
        {[...Array(12)].map((_, i) => (
          <ShimmerSkeleton key={i} className="aspect-square rounded-lg" />
        ))}
      </div>
    )
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-20">
        <svg
          className="w-16 h-16 mx-auto text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
          />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-700">No photos found</h3>
        <p className="mt-2 text-sm text-gray-500">
          There are no photos to display.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        {renderToolbar()}

        {layoutMode === 'grid' ? renderGrid() : renderMasonry()}

        <div ref={sentinelRef} className="h-10 flex justify-center items-center">
          {isFetchingNextPage && <Loading size="sm" />}
        </div>
      </div>

      {/* Bulk download bar - appears when photos are selected */}
      {hasSelection && (
        <BulkDownloadBar
          selectedImages={selectedImagesMap}
          onClearSelection={handleClearSelection}
        />
      )}

      <Lightbox
        images={images}
        currentIndex={currentImageIndex}
        isOpen={lightboxOpen}
        onClose={handleLightboxClose}
        onNavigate={handleLightboxNavigate}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPageError={error}
      />
    </>
  )
}

